/*
 * A search field with a clear button that gives focus back.
 *
 * The focus order is the whole point of this component. When the clear button
 * empties the field it also removes itself, because a clear button over an
 * empty field is a target that does nothing. If focus is still on it at that
 * moment the browser drops focus to `<body>`, and a keyboard user is silently
 * returned to the top of the document: their next Tab starts from the skip
 * link, not from the field they were using. So the input is focused first and
 * the value cleared second, in that order.
 *
 * `type="search"` for the semantics, with the platform's own clear button
 * suppressed in CSS. Two clear buttons on one field, one of them styled and one
 * of them not, is worse than either.
 */

import { useId, useRef } from 'react'
import type { InputHTMLAttributes } from 'react'
import { joinIds } from '../aria'
import { useFieldControl } from '../Field/fieldContext'
import { CrossMark, SearchMark } from '../marks'
import styles from './SearchInput.module.css'

export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  value: string
  onValueChange(next: string): void
  /**
   * The accessible name, used when the component is not inside a Field. A
   * placeholder is not a label: it disappears the moment somebody types, and it
   * is not announced by every screen reader in the first place.
   */
  label?: string
  clearLabel?: string
}

export function SearchInput({
  value,
  onValueChange,
  label = 'Search',
  clearLabel = 'Clear search',
  className,
  ...rest
}: SearchInputProps) {
  const field = useFieldControl()
  const generatedId = useId()
  const id = rest.id ?? field?.id ?? generatedId
  const inputRef = useRef<HTMLInputElement>(null)

  function handleClear() {
    inputRef.current?.focus()
    onValueChange('')
  }

  const rootClassName = [styles.wrap, className].filter(Boolean).join(' ')

  return (
    <div className={rootClassName}>
      <SearchMark className={styles.lens} />
      <input
        {...rest}
        ref={inputRef}
        type="search"
        id={id}
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        className={styles.input}
        aria-label={field ? undefined : (rest['aria-label'] ?? label)}
        aria-describedby={joinIds(rest['aria-describedby'], field?.describedBy)}
      />
      {value.length > 0 ? (
        <button type="button" className={styles.clear} onClick={handleClear} aria-label={clearLabel}>
          <CrossMark size={14} />
        </button>
      ) : null}
    </div>
  )
}
